// ============================================================================
// TAX SUMMARY BY CLASSIFICATION (ADR-0047 Phase 2 §8)
//
// The "HSN-wise summary" under India GST; by rate alone under a regime that
// declares no classification (Botswana VAT). One row per jurisdiction /
// classification / unit / headline rate / treatment with quantity, taxable
// value and the tax split by component (CGST, SGST, IGST … or VAT).
//
// Reads ONLY the document tax snapshots written when each invoice or credit
// note posted — it never re-prices from today's masters, so a rate change
// after posting cannot move a closed period. Amounts are base currency at the
// document's posting rate (the same basis as Item-wise Sales and the GL).
//
// Credit notes: netted against invoices by default (the sign column), or shown
// alone through the Documents filter. Filters take effect on Apply, like the
// other report screens.
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService } from 'api/services';
import type { TaxSummaryResultDto, TaxSummaryRowDto, TaxSummaryQueryParams, TaxSummaryDocumentType } from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import ReportHeader from 'components/reports/ReportHeader';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: number) => (n === 0 ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 4 }));
const fmtRate = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}%`;

const DOC_TYPE_OPTIONS: SelectOption[] = [
  { value: 'ALL', label: 'Invoices net of credit notes' },
  { value: 'SALES_INVOICE', label: 'Invoices only' },
  { value: 'CREDIT_NOTE', label: 'Credit notes only' },
];
const docTypeLabel = (t: TaxSummaryDocumentType) => DOC_TYPE_OPTIONS.find(o => o.value === t)?.label ?? t;

const treatmentLabel = (t: string) => {
  switch (t) {
    case 'TAXABLE': return 'Taxable';
    case 'EXEMPT': return 'Exempt';
    case 'ZERO_RATED': return 'Zero-rated';
    case 'OUT_OF_SCOPE': return 'Out of scope';
    default: return t ? t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' ') : '—';
  }
};

interface AppliedFilters {
  fromDate: string; toDate: string; documentType: TaxSummaryDocumentType;
  entityIds: string[]; businessUnitIds: string[];
}

interface TaxSummaryScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const TaxSummaryScreen: React.FC<TaxSummaryScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('tax-summary');
  const { accessibleEntities, accessibleBusinessUnits } = useAuthContext();

  // --- draft filters ---
  const [fromDate, setFromDate] = useState<string>(monthStart());
  const [toDate, setToDate] = useState<string>(today());
  const [documentType, setDocumentType] = useState<TaxSummaryDocumentType>('ALL');
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedBuIds, setSelectedBuIds] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // --- applied filters: the report reflects ONLY what was applied ---
  const [applied, setApplied] = useState<AppliedFilters>(
    { fromDate: monthStart(), toDate: today(), documentType: 'ALL', entityIds: [], businessUnitIds: [] },
  );

  const [data, setData] = useState<TaxSummaryResultDto | null>(null);
  const [loading, setLoading] = useState(false);

  const entityOptions: SelectOption[] = useMemo(
    () => accessibleEntities.map(e => ({ value: e.id, label: `${e.entityCode} — ${e.entityName}` })),
    [accessibleEntities],
  );
  const buOptions: SelectOption[] = useMemo(() => {
    const scoped = selectedEntityIds.length > 0
      ? accessibleBusinessUnits.filter(b => selectedEntityIds.includes(b.entityId))
      : accessibleBusinessUnits;
    return scoped.map(b => ({ value: b.id, label: b.buName }));
  }, [accessibleBusinessUnits, selectedEntityIds]);

  useEffect(() => {
    if (selectedEntityIds.length === 0 || selectedBuIds.length === 0) return;
    const allowed = new Set(buOptions.map(o => String(o.value)));
    const next = selectedBuIds.filter(id => allowed.has(id));
    if (next.length !== selectedBuIds.length) setSelectedBuIds(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityIds]);

  const appliedParams = useCallback((): TaxSummaryQueryParams => ({
    fromDate: applied.fromDate, toDate: applied.toDate, documentType: applied.documentType,
    entityIds: applied.entityIds.length > 0 ? applied.entityIds : undefined,
    businessUnitIds: applied.businessUnitIds.length > 0 ? applied.businessUnitIds : undefined,
  }), [applied]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await reportService.getTaxSummary(appliedParams()));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the report.');
      setData(null);
    } finally { setLoading(false); }
  }, [appliedParams]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = useCallback(() => {
    if (!fromDate || !toDate) { notificationService.toast.warning('Period (From / To) is required.'); return; }
    if (fromDate > toDate) { notificationService.toast.warning('From date must be on or before To date.'); return; }
    setApplied({ fromDate, toDate, documentType, entityIds: selectedEntityIds, businessUnitIds: selectedBuIds });
    setFilterOpen(false);
  }, [fromDate, toDate, documentType, selectedEntityIds, selectedBuIds]);

  const isStale = useMemo(
    () => JSON.stringify({ f: fromDate, t: toDate, d: documentType, e: selectedEntityIds, b: selectedBuIds })
       !== JSON.stringify({ f: applied.fromDate, t: applied.toDate, d: applied.documentType, e: applied.entityIds, b: applied.businessUnitIds }),
    [fromDate, toDate, documentType, selectedEntityIds, selectedBuIds, applied],
  );

  const handleExport = useCallback(async () => {
    try { await reportService.exportTaxSummary(appliedParams()); notificationService.toast.success('Export downloaded.'); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message || 'Could not export.'); }
  }, [appliedParams]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.refresh(load) },
    ...(can('EXPORT') ? [{ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', onClick: handleExport, variant: 'icon' as const }] : []),
  ];

  const ccy = data?.baseCurrencyCode || '';
  const componentCodes = data?.componentCodes ?? [];
  const showJurisdiction = (data?.jurisdictionCodes.length ?? 0) > 1;
  const appliedCount = (applied.entityIds.length ? 1 : 0) + (applied.businessUnitIds.length ? 1 : 0) + (applied.documentType !== 'ALL' ? 1 : 0);
  const money = (v: number) => (
    <span style={{ display: 'block', textAlign: 'right', color: v < 0 ? 'var(--danger-color, #dc2626)' : undefined }}>{fmtMoney(v)}</span>
  );
  const componentAmount = (row: TaxSummaryRowDto, code: string) =>
    row.components.find(c => c.componentCode === code)?.taxAmount ?? 0;
  const colCount = (showJurisdiction ? 1 : 0) + 7 + componentCodes.length + 1;

  return (
    <EnterpriseScreenShell
      title="Tax Summary"
      description="Tax by classification (HSN / SAC), unit and rate, from the document tax snapshots"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={toDate} onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Documents" options={DOC_TYPE_OPTIONS} value={documentType}
                  onChange={v => setDocumentType(((v as string) || 'ALL') as TaxSummaryDocumentType)}
                  size="sm" menuPortalTarget={document.body} />
              </div>
              {entityOptions.length > 1 && (
                <div className="filter-section">
                  <SyutSelect label="Entities" isMulti options={entityOptions} value={selectedEntityIds}
                    onChange={v => setSelectedEntityIds((v as string[]) || [])}
                    placeholder="All my entities" size="sm" menuPortalTarget={document.body} />
                </div>
              )}
              <div className="filter-section">
                <SyutSelect label="Business Units" isMulti options={buOptions} value={selectedBuIds}
                  onChange={v => setSelectedBuIds((v as string[]) || [])}
                  placeholder="All business units" size="sm" menuPortalTarget={document.body} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setSelectedEntityIds([]); setSelectedBuIds([]); setDocumentType('ALL'); setFromDate(monthStart()); setToDate(today());
              }}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'Period', value: `${applied.fromDate} → ${applied.toDate}` },
          { label: 'Documents', value: docTypeLabel(applied.documentType) },
          { label: 'Entities', value: applied.entityIds.length ? `${applied.entityIds.length} selected` : 'All' },
          { label: 'Business Units', value: applied.businessUnitIds.length ? `${applied.businessUnitIds.length} selected` : 'All' },
          { label: `Taxable Value ${ccy}`, value: data?.mixedBaseCurrency ? 'Mixed' : fmtMoney(data?.totalTaxableValue) },
          { label: `Total Tax ${ccy}`, value: data?.mixedBaseCurrency ? 'Mixed' : fmtMoney(data?.totalTaxAmount) },
        ]}
      />

      {data?.mixedBaseCurrency && (
        <div style={{ margin: '0.5rem 1rem', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)', fontSize: '0.8125rem' }}>
          The selected entities use different functional currencies, so totals are not combined.
          Filter to entities sharing one base currency to see totals.
        </div>
      )}

      {isStale && (
        <div style={{
          margin: '0.5rem 1rem', padding: '0.4rem 0.75rem', borderRadius: '0.5rem',
          background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
          fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <IconSystem.info size={14} />
          <span>Filters changed — click <strong>Apply</strong> to refresh the report.</span>
        </div>
      )}

      {!!data?.backfilledLineCount && (
        <div style={{ margin: '0.5rem 1rem', padding: '0.4rem 0.75rem', borderRadius: '0.5rem', background: 'var(--bg-secondary, #f1f5f9)', color: 'var(--text-secondary)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconSystem.info size={14} />
          <span>
            {data.backfilledLineCount} line{data.backfilledLineCount === 1 ? '' : 's'} come from documents posted before tax snapshots existed;
            they carry no classification or unit and appear under <strong>(none)</strong>.
          </span>
        </div>
      )}

      <div className="data-grid-container" style={{ overflow: 'auto', padding: '0 1rem' }}>
        <table className="syut-grid" style={{ width: '100%' }}>
          <thead>
            <tr>
              {showJurisdiction && <th>Jurisdiction</th>}
              <th style={{ minWidth: 220 }}>Classification</th>
              <th>Unit</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              <th>Treatment</th>
              <th style={{ textAlign: 'right' }}>Docs</th>
              <th style={{ textAlign: 'right' }}>Quantity</th>
              <th style={{ textAlign: 'right' }}>Taxable Value</th>
              {componentCodes.map(code => <th key={code} style={{ textAlign: 'right' }}>{code}</th>)}
              <th style={{ textAlign: 'right' }}>Total Tax</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows.length ?? 0) === 0 && (
              <tr><td colSpan={colCount} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
                {loading ? 'Loading...' : 'No taxed documents in the selected period.'}
              </td></tr>
            )}
            {data?.rows.map((r, i) => (
              <tr key={`${r.jurisdictionCode}|${r.classificationCode ?? ''}|${r.unitCode ?? ''}|${r.headlineRate}|${r.taxTreatment}|${i}`}>
                {showJurisdiction && <td>{r.jurisdictionCode}</td>}
                <td>
                  <div style={{ fontWeight: 600 }}>{r.classificationCode ?? '(none)'}</div>
                  {r.classificationDescription && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.classificationDescription}</div>
                  )}
                </td>
                <td title={r.unitName ?? undefined}>{r.unitCode ?? ''}</td>
                <td style={{ textAlign: 'right' }}>{fmtRate(r.headlineRate)}</td>
                <td>{treatmentLabel(r.taxTreatment)}</td>
                <td style={{ textAlign: 'right' }}>{r.documentCount}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.quantity)}</td>
                <td>{money(r.taxableValue)}</td>
                {componentCodes.map(code => <td key={code}>{money(componentAmount(r, code))}</td>)}
                <td style={{ fontWeight: 600 }}>{money(r.taxAmount)}</td>
              </tr>
            ))}
          </tbody>
          {data && !data.mixedBaseCurrency && data.rows.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border-color)' }}>
                <td colSpan={(showJurisdiction ? 1 : 0) + 4}>Total</td>
                <td style={{ textAlign: 'right' }}>{data.totalDocumentCount}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(data.totalQuantity)}</td>
                <td>{money(data.totalTaxableValue)}</td>
                {componentCodes.map(code => <td key={code}>{money(data.componentTotals[code] ?? 0)}</td>)}
                <td>{money(data.totalTaxAmount)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </EnterpriseScreenShell>
  );
};

export default TaxSummaryScreen;
